// UI telemetry for the Safety Dashboard (product analytics, phase 1).
//
// Modelled on usageSession.js: best-effort, never throws, never blocks the UI.
// A dropped batch is acceptable loss — this is analytics, not billing.
//
// What it sends: which mode and data source are open, which panels are visible
// and for how long, which controls are clicked, and which filters are set. It
// rides the EXISTING login session (`rsaUsageSessionId` in localStorage, minted
// by usageSession.openUsageSession) so every event can be placed inside a known
// login → logout window without a second session concept.
//
// What it never sends: keystrokes, typed text (a search records "used" and a
// length bucket, never the content), form values, clipboard, IP, device or
// browser strings. See §3.5 of docs/architecture/safety_dashboard_telemetry_plan.md.
import { authHeaders } from './authHeader';

const ENDPOINT = '/api/telemetry/events';
const SESSION_ID_KEY = 'rsaUsageSessionId';

const FLUSH_EVENTS = 25;
const FLUSH_MS = 10000;
// A batch must stay under the PROD WAF's 8 KB body limit even if the Allow rule
// regresses (infra/waf/telemetry_allow_fix.py). 6 KB leaves room for the wrapper.
const MAX_BATCH_BYTES = 6000;
const MAX_BEACON_BYTES = 56000;
const IDLE_MS = 60000;          // no input for this long → stop counting time
const MIN_DWELL_MS = 1000;      // spans shorter than this aren't worth a row

let app = 'safety_dashboard';
let enabled = false;
let queue = [];
let pendingFilterStates = [];
let sentHashes = new Set();
let seq = 0;
let flushTimer = null;

let ctx = { mode: null, source: null, filterHash: null };

// --- stable hashing -------------------------------------------------------

/** Deterministic JSON: keys sorted, empties dropped, so the same filter set
 *  always hashes the same regardless of how React happened to build the object. */
function stable(value) {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    const items = value.map(stable).filter((v) => v !== null);
    return items.length ? items.slice().sort((a, b) => (JSON.stringify(a) < JSON.stringify(b) ? -1 : 1)) : null;
  }
  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).sort().forEach((k) => {
      const v = stable(value[k]);
      if (v !== null && v !== '' && !(Array.isArray(v) && !v.length)) out[k] = v;
    });
    return Object.keys(out).length ? out : null;
  }
  return value;
}

/** 64-bit FNV-1a as 16 hex chars — two 32-bit passes with different offsets.
 *  A 32-bit hash would collide at a percent or so across a few thousand
 *  distinct filter sets, which would silently merge two combinations in the
 *  report; 64 bits makes that negligible. */
function hashOf(obj) {
  const str = JSON.stringify(stable(obj) || {});
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < str.length; i += 1) {
    const c = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x85ebca6b) >>> 0;
  }
  return (h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0'));
}

// --- transport ------------------------------------------------------------

function sessionId() {
  try {
    return localStorage.getItem(SESSION_ID_KEY) || null;
  } catch {
    return null;
  }
}

function envelope(events, states) {
  return {
    app,
    session_id: sessionId(),
    events,
    ...(states.length ? { filter_states: states } : {}),
  };
}

/** Largest prefix of `events` whose payload stays under `limit` bytes. */
function fitBatch(events, limit) {
  return fitBody(events, [], limit).events;
}

/** The events AND filter-state registrations that fit in one request.
 *
 *  Measures the WHOLE body, which is the point: the WAF in front of PROD blocks
 *  any request body over 8 KB, and this used to size `{events}` alone while the
 *  filter snapshots rode along unmeasured. A few rapid filter changes queue
 *  several 31-key snapshots, so the real body could cross the limit and come
 *  back 403 — on PROD only, because the DEV WebACL merely counts that rule.
 *  That failure is invisible: the batch is dropped and the dashboard just shows
 *  less activity than happened.
 *
 *  Filter states are shed BEFORE events because they are the cheap thing to
 *  defer: every event carries only its filter_hash, and the server upserts
 *  states by hash, so a registration that arrives in a later request still
 *  joins to the events that referenced it. Order does not matter; loss does.
 */
function fitBody(events, states, limit) {
  const size = (e, s) => JSON.stringify(envelope(e, s)).length;
  let n = Math.min(events.length, FLUSH_EVENTS);
  let s = states.length;
  while (s > 0 && size(events.slice(0, n), states.slice(0, s)) > limit) s -= 1;
  while (n > 1 && size(events.slice(0, n), states.slice(0, s)) > limit) n = Math.floor(n / 2);
  // One snapshot too big for a whole request on its own: send it alone rather
  // than never. Dropping it would leave a trace step whose filters read as
  // unknown forever, and alone it gets the entire budget.
  if (s === 0 && states.length && !events.length) s = 1;
  return { events: events.slice(0, n), states: states.slice(0, s) };
}

async function flush() {
  if (!enabled || (!queue.length && !pendingFilterStates.length)) return;
  const fit = fitBody(queue, pendingFilterStates, MAX_BATCH_BYTES);
  if (!fit.events.length && !fit.states.length) return;
  queue = queue.slice(fit.events.length);
  pendingFilterStates = pendingFilterStates.slice(fit.states.length);
  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(envelope(fit.events, fit.states)),
    });
  } catch {
    /* best-effort: the batch is gone, which is fine */
  }
  // Anything shed to keep the body legal goes out in the next request rather
  // than waiting for the next event to happen to trigger a flush.
  if (queue.length >= FLUSH_EVENTS || pendingFilterStates.length) flush();
}

/** Unload path: fetch is unreliable there, so one sendBeacon with whatever fits.
 *  No auth header — sendBeacon cannot set one; the endpoint requires auth, so a
 *  beacon is a best-effort extra rather than the main path. That is why the
 *  visibilitychange flush above (which CAN authenticate) does the real work. */
function flushBeacon() {
  if (!enabled || !queue.length) return;
  try {
    const fit = fitBody(queue, pendingFilterStates, MAX_BEACON_BYTES);
    const blob = new Blob([JSON.stringify(envelope(fit.events, fit.states))],
      { type: 'application/json' });
    navigator.sendBeacon(ENDPOINT, blob);
    queue = queue.slice(fit.events.length);
    pendingFilterStates = pendingFilterStates.slice(fit.states.length);
  } catch {
    /* best-effort */
  }
}

function schedule() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_MS);
}

// --- event emission -------------------------------------------------------

/** Queue one event. `kind` must be one of the kinds telemetry_catalog.KINDS
 *  accepts; anything else is dropped server-side. */
export function track(kind, target, value, extra) {
  if (!enabled || !target) return;
  seq += 1;
  queue.push({
    kind,
    target,
    ts: new Date().toISOString(),
    seq,
    mode: ctx.mode || undefined,
    source: ctx.source || undefined,
    filter_hash: ctx.filterHash || undefined,
    ...(value === undefined ? {} : { value }),
    ...(extra || {}),
  });
  if (queue.length >= FLUSH_EVENTS) flush();
  else schedule();
}

// --- time accounting ------------------------------------------------------
// Active = the tab is visible AND the user has done something in the last
// IDLE_MS. A dashboard left open on a second monitor must not read as
// engagement, so dwell stops accumulating rather than counting wall clock.

let visible = true;
let lastInput = Date.now();
let spans = new Map();          // target -> {visible, since, acc}

function isActive() {
  return visible && Date.now() - lastInput < IDLE_MS;
}

function spanFor(target) {
  let s = spans.get(target);
  if (!s) {
    s = { visible: false, since: null, acc: 0 };
    spans.set(target, s);
  }
  return s;
}

function stopSpan(target, emit) {
  const s = spans.get(target);
  if (!s) return;
  if (s.since !== null) {
    s.acc += Date.now() - s.since;
    s.since = null;
  }
  if (emit && s.acc >= MIN_DWELL_MS) {
    track('view', target, undefined, { dwell_ms: Math.round(s.acc) });
    s.acc = 0;
  }
}

function recompute() {
  const active = isActive();
  spans.forEach((s, target) => {
    if (s.visible && active) {
      if (s.since === null) s.since = Date.now();
    } else {
      stopSpan(target, false);
    }
  });
}

/** Flush every open span as a `view` event — on unload, on a mode/source change,
 *  and when the tab is hidden. */
function flushSpans() {
  Array.from(spans.keys()).forEach((t) => stopSpan(t, true));
}

/** Mark a target visible or not. Panels call this from an IntersectionObserver. */
export function setVisible(target, isVisible) {
  if (!enabled || !target) return;
  const s = spanFor(target);
  if (s.visible === isVisible) return;
  s.visible = isVisible;
  if (!isVisible) stopSpan(target, true);
  recompute();
}

// --- context --------------------------------------------------------------

/** The dashboard's mode and data source. A change ends the previous mode's dwell
 *  span and emits the switch, so mode time and mode switches are both real. */
export function setContext({ mode, source } = {}) {
  if (!enabled) return;
  const nextMode = mode === undefined ? ctx.mode : mode;
  const nextSource = source === undefined ? ctx.source : source;

  if (nextMode !== ctx.mode) {
    if (ctx.mode) {
      setVisible(`mode.${ctx.mode}`, false);
    }
    ctx.mode = nextMode;
    if (nextMode) {
      track('mode', `mode.${nextMode}`);
      setVisible(`mode.${nextMode}`, true);
    }
  }
  if (nextSource !== ctx.source) {
    ctx.source = nextSource;
    if (nextSource) track('source', 'start.source_pick', nextSource);
  }
}

/** Register the current filter set. The full object is uploaded ONCE per
 *  distinct combination; every event afterwards carries only its hash. */
export function setFilters(filters) {
  if (!enabled) return null;
  const normalized = stable(filters) || {};
  const h = hashOf(normalized);
  ctx.filterHash = h;
  if (!sentHashes.has(h)) {
    sentHashes.add(h);
    pendingFilterStates.push({ filter_hash: h, filters: normalized });
    schedule();
  }
  return h;
}

/** Emit one `filter` event per changed key between two flat settings objects.
 *  Keeps the diffing out of the dashboard: it hands over a flat snapshot and
 *  this works out what moved. Values are coerced to small scalars — a long
 *  array becomes its length, never its contents. */
export function trackFilterDiff(prev, next) {
  if (!enabled || !next) return;
  if (prev === null || prev === undefined) return;     // first snapshot isn't a change
  Object.keys(next).forEach((key) => {
    const a = prev[key];
    const b = next[key];
    if (JSON.stringify(a) === JSON.stringify(b)) return;
    track('filter', key, describeChange(a, b));
  });
}

const MAX_CHANGE_LEN = 110;

/**
 * What the user actually did to a setting, in words.
 *
 * A reader of the trace needs "+ISD" or "street → satellite", not "3 selected"
 * or "changed": the point of the trace is to follow someone's reasoning, and a
 * count tells you nothing about what they picked. Falls back to a count only
 * when naming the items would be longer than the line can hold.
 */
function describeChange(before, after) {
  if (Array.isArray(before) || Array.isArray(after)) {
    const was = new Set((before || []).map(String));
    const now = new Set((after || []).map(String));
    const added = [...now].filter((x) => !was.has(x));
    const removed = [...was].filter((x) => !now.has(x));
    const parts = [];
    if (added.length) parts.push(`+${added.join(', ')}`);
    if (removed.length) parts.push(`−${removed.join(', ')}`);
    const text = parts.join('  ') || 'changed';
    return text.length <= MAX_CHANGE_LEN ? text : `${now.size} selected`;
  }
  if (before && after && typeof before === 'object' && typeof after === 'object') {
    const changed = Object.keys({ ...before, ...after })
      .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]));
    return changed.length ? changed.join(', ').slice(0, MAX_CHANGE_LEN) : 'changed';
  }
  if (typeof after === 'boolean') return after ? 'on' : 'off';
  if (after === null || after === undefined || after === '') return 'cleared';
  const from = (before === null || before === undefined || before === '') ? '' : `${before} → `;
  return `${from}${after}`.slice(0, MAX_CHANGE_LEN);
}

// --- clicks ---------------------------------------------------------------
// One delegated listener reads `data-track` off the nearest ancestor that has
// it, so instrumenting a control is one attribute rather than a wrapper.
// `data-track-kind` overrides the kind, `data-track-value` adds a value.

function onClick(e) {
  try {
    const el = e.target && e.target.closest ? e.target.closest('[data-track]') : null;
    if (!el) return;
    const target = (el.getAttribute('data-track') || '').trim().toLowerCase();
    if (!target) return;
    const kind = (el.getAttribute('data-track-kind') || 'click').trim();
    const raw = el.getAttribute('data-track-value');
    track(kind, target, raw === null ? undefined : raw);
  } catch {
    /* never let instrumentation break a click */
  }
}

function onInput() {
  const wasActive = isActive();
  lastInput = Date.now();
  if (!wasActive) recompute();
}

// --- scrolling -------------------------------------------------------------
// How far down the page they actually read, and when they come back up.
// Reported in quarters rather than per pixel: "scrolled to the table and back"
// is the question, not the scrollbar's exact position. Scroll events do not
// bubble, so the listener is registered in the capture phase to catch them from
// whichever element is actually scrolling.

const SCROLL_STEPS = [25, 50, 75, 100];
let scrollDeepest = 0;
let scrollLast = 0;
let scrollTimer = null;

function scrollDepthOf(target) {
  const el = (target && target.scrollingElement) || target || document.documentElement;
  const top = el.scrollTop ?? window.scrollY ?? 0;
  const height = (el.scrollHeight || 0) - (el.clientHeight || window.innerHeight || 0);
  if (height <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((top / height) * 100)));
}

function onScroll(e) {
  if (!enabled) return;
  lastInput = Date.now();
  if (scrollTimer) return;              // coalesce a burst into one reading
  scrollTimer = setTimeout(() => {
    scrollTimer = null;
    let depth;
    try {
      depth = scrollDepthOf(e.target);
    } catch {
      return;
    }
    const step = SCROLL_STEPS.filter((s) => depth >= s).pop() || 0;
    const deepestStep = SCROLL_STEPS.filter((s) => scrollDeepest >= s).pop() || 0;
    if (step > deepestStep) {
      track('view', 'scroll.depth', `${step}%`);
    } else if (scrollLast - depth >= 25) {
      // A real return upwards, not the jitter of a trackpad.
      track('view', 'scroll.up', `${depth}%`);
    }
    scrollDeepest = Math.max(scrollDeepest, depth);
    scrollLast = depth;
  }, 400);
}

function onVisibility() {
  visible = document.visibilityState !== 'hidden';
  if (!visible) {
    flushSpans();
    flush();          // authenticated flush while we still can
  } else {
    lastInput = Date.now();
  }
  recompute();
}

function onUnload() {
  flushSpans();
  flushBeacon();
}

let idleTicker = null;

/** Start capture. Safe to call twice; returns a stop function. */
export function init(options = {}) {
  if (enabled) return stop;
  app = options.app || app;
  enabled = true;
  queue = [];
  pendingFilterStates = [];
  sentHashes = new Set();
  spans = new Map();
  seq = 0;
  ctx = { mode: null, source: null, filterHash: null };
  visible = document.visibilityState !== 'hidden';
  lastInput = Date.now();
  scrollDeepest = 0;
  scrollLast = 0;

  document.addEventListener('click', onClick, true);
  ['pointerdown', 'keydown', 'wheel'].forEach((ev) =>
    window.addEventListener(ev, onInput, { passive: true }));
  document.addEventListener('scroll', onScroll, true);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onUnload);
  window.addEventListener('beforeunload', onUnload);
  // Closes an open span when the user simply walks away: without this, the span
  // would keep its start time and the next flush would bill the idle minutes.
  idleTicker = setInterval(recompute, 15000);
  return stop;
}

export function stop() {
  if (!enabled) return;
  flushSpans();
  flush();
  document.removeEventListener('click', onClick, true);
  ['pointerdown', 'keydown', 'wheel'].forEach((ev) =>
    window.removeEventListener(ev, onInput));
  document.removeEventListener('scroll', onScroll, true);
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('pagehide', onUnload);
  window.removeEventListener('beforeunload', onUnload);
  if (idleTicker) clearInterval(idleTicker);
  if (flushTimer) clearTimeout(flushTimer);
  if (scrollTimer) clearTimeout(scrollTimer);
  scrollTimer = null;
  idleTicker = null;
  flushTimer = null;
  enabled = false;
}

export const _test = { hashOf, stable, fitBatch, fitBody, describeChange, scrollDepthOf };
